import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, BadgeIndianRupee, ClipboardList, XCircle, CheckCircle2, Banknote } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = {
  new_task: { icon: ClipboardList, cls: "bg-blue-50 text-blue-600" },
  verified: { icon: CheckCircle2, cls: "bg-green-50 text-green-600" },
  rejected: { icon: XCircle, cls: "bg-red-50 text-red-600" },
  reward_paid: { icon: BadgeIndianRupee, cls: "bg-emerald-50 text-emerald-600" },
  reward_adjusted: { icon: BadgeIndianRupee, cls: "bg-violet-50 text-violet-600" },
  withdrawal_processing: { icon: Banknote, cls: "bg-sky-50 text-sky-600" },
  withdrawal_paid: { icon: Banknote, cls: "bg-green-50 text-green-600" },
  withdrawal_rejected: { icon: Banknote, cls: "bg-red-50 text-red-600" },
};

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const prevUnread = useRef(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    if (!user) return;
    try {
      const { data: d } = await api.get("/user/notifications");
      if (d.unread > prevUnread.current && typeof Notification !== "undefined" && Notification.permission === "granted") {
        const latest = d.notifications.find((n) => !n.read);
        if (latest) new Notification(latest.title, { body: latest.message });
      }
      prevUnread.current = d.unread;
      setData(d);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onDoc); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;

  const markAll = async () => {
    await api.post("/user/notifications/read", { all: true });
    load();
  };

  const openItem = (n) => {
    if (!n.read) api.post("/user/notifications/read", { ids: [n.id] }).then(load);
    setOpen(false);
    navigate(n.link || "/");
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell-btn" onClick={() => setOpen(!open)} aria-label="Notifications"
        className="relative p-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors">
        <Bell className="w-5 h-5" />
        {data.unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="fixed left-3 right-3 top-[68px] sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[330px] bg-white rounded-2xl border border-slate-200 shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-heading font-bold text-slate-900 text-sm">Notifications</h3>
            {data.unread > 0 && (
              <button onClick={markAll} data-testid="mark-all-read-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-800">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {data.notifications.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notifications-empty">No notifications yet.</p>
            ) : (
              data.notifications.map((n) => {
                const meta = ICONS[n.type] || { icon: Bell, cls: "bg-slate-100 text-slate-600" };
                return (
                  <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`}
                    className={`w-full text-left flex gap-3 px-4 py-3.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? "bg-blue-50/40" : ""}`}>
                    <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${meta.cls}`}>
                      <meta.icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm truncate ${!n.read ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}>{n.title}</p>
                        {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-blue-600" />}
                      </div>
                      <p className="text-xs text-slate-600 leading-snug mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{fmtDate(n.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
